import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import styles from './AuthField.module.css';

interface AuthFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon?: React.ReactNode;
  error?: string;
  hint?: string;
  reveal?: boolean;
}

/** Campo único de autenticação para PC e PWA, com foco, erro e password reveal. */
const AuthField: React.FC<AuthFieldProps> = ({ label, icon, error, hint, reveal, type, id, className = '', onKeyDown, ...rest }) => {
  const [show, setShow] = useState(false);
  const fieldId = id || `auth-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const messageId = `${fieldId}-message`;
  const inputType = reveal ? (show ? 'text' : 'password') : type;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || rest.enterKeyHint !== 'next' || event.key !== 'Enter' || !event.currentTarget.form) return;
    event.preventDefault();
    const fields = Array.from(
      event.currentTarget.form.querySelectorAll<HTMLElement>('input:not([type=hidden]):not([disabled]), select, textarea'),
    );
    fields[fields.indexOf(event.currentTarget) + 1]?.focus();
  };

  return (
    <div className={`${styles.field} ${className}`.trim()}>
      <label htmlFor={fieldId} className={styles.label}>{label}</label>
      <div className={`${styles.control} ${error ? styles.controlError : ''}`}>
        {icon && <span className={styles.icon} aria-hidden="true">{icon}</span>}
        <input
          {...rest}
          id={fieldId}
          type={inputType}
          aria-invalid={Boolean(error)}
          aria-describedby={error || hint ? messageId : undefined}
          onKeyDown={handleKeyDown}
          className={styles.input}
        />
        {reveal && (
          <button
            type="button"
            className={styles.reveal}
            onClick={() => setShow((value) => !value)}
            aria-label={show ? 'Esconder password' : 'Mostrar password'}
          >
            {show ? <EyeOff size={19} /> : <Eye size={19} />}
          </button>
        )}
      </div>
      {(error || hint) && (
        <span id={messageId} className={error ? styles.error : styles.hint} role={error ? 'alert' : undefined}>
          {error || hint}
        </span>
      )}
    </div>
  );
};

export default AuthField;
